import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { LinksIDService } from '../services/api';
import { auth } from '../services/firebase';
import { History, RefreshCw, Download, ChevronDown, Crosshair, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardBody } from '../components/ds/Card';
import { Button } from '../components/ds/Button';
import { AuthContext } from '../contexts/AuthContext';

export default function HistoryPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const { user, passport } = useContext(AuthContext);

  const loadHistory = async () => {
    setLoading(true);

    // For authenticated users, try LinksID first
    if (user && !user.isGuest && passport && auth?.currentUser) {
      try {
        const token = await auth.currentUser.getIdToken();
        const linksidSessions = await LinksIDService.getSessions(token);
        const mapped = (linksidSessions || []).map(s => ({
          id: s.id,
          date: s.started_at,
          club_type: s.device_name || s.session_type || 'Session',
          session_type: s.session_type === 'range' ? 'analytics' : (s.session_type === 'fitting' ? 'fitter' : s.session_type),
          club_speed: s.avg_club_speed_mph,
          ball_speed: s.avg_ball_speed_mph,
          launch_angle: s.avg_launch_angle_deg,
          spin_rate: s.avg_spin_rate_rpm,
          carry: s.avg_carry_yards ? Math.round(s.avg_carry_yards) : null,
          head: null,
          shaft: null,
          input_method: s.source_platform || 'linksid',
          shot_count: s.shot_count || 0,
          source: 'linksid',
        }));
        setSessions(mapped);
        setSyncStatus('synced');
        setLoading(false);
        return;
      } catch (err) {
        console.warn('[LinksID] Sessions read failed, falling back to local:', err.message);
        setSyncStatus('local');
      }
    }

    // Fallback: read from PocketFitter local
    axios.get(`/history`).then(r => setSessions(r.data.items || [])).finally(() => setLoading(false));
  };

  useEffect(() => { loadHistory(); }, [user]);

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!confirm('Delete this session?')) return;
    const session = sessions.find(s => s.id === id);
    if (session?.source === 'linksid' && auth?.currentUser) {
      const token = await auth.currentUser.getIdToken();
      await LinksIDService.deleteSession(token, id);
    } else {
      await axios.delete(`/history/${id}`);
    }
    if (expandedSession === id) setExpandedSession(null);
    loadHistory();
  };

  const downloadSession = async (id) => {
    const session = sessions.find(s => s.id === id);
    try {
      // LinksID sessions: fetch shots from LinksID and generate CSV client-side
      if (session?.source === 'linksid' && auth?.currentUser) {
        const token = await auth.currentUser.getIdToken();
        const shots = await LinksIDService.getSessionShots(token, id);
        const headers = ['Shot #', 'Club Speed (mph)', 'Ball Speed (mph)', 'Launch Angle (deg)', 'Spin Rate (rpm)', 'Carry (yards)', 'Total (yards)', 'Smash Factor'];
        const rows = (shots || []).map(s => [
          s.sequence, s.club_speed_mph || '', s.ball_speed_mph || '', s.launch_angle_deg || '',
          s.spin_rate_rpm || '', s.carry_yards || '', s.total_yards || '', s.smash_factor || ''
        ]);
        const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `linksid_session_${id}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      // Local sessions: use server-side export
      const response = await axios.get(`/history/${id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `pocketfitter_session_${id}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to download session', e);
      alert('Failed to download session data');
    }
  };

  // Bulk export all sessions as CSV
  const exportAllSessions = () => {
    if (sessions.length === 0) {
      alert('No sessions to export');
      return;
    }
    // Create CSV content
    const headers = ['Date', 'Club Type', 'Club Speed', 'Ball Speed', 'Launch Angle', 'Spin Rate', 'Carry', 'Input Method', 'Recommended Head', 'Recommended Shaft'];
    const rows = sessions.map(s => [
      s.date,
      s.club_type,
      s.club_speed || '',
      s.ball_speed || '',
      s.launch_angle || '',
      s.spin_rate || '',
      s.carry || '',
      s.input_method || 'manual',
      s.recommended_head || '',
      s.recommended_shaft || ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pocketfitter_history_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Handle "Open in Fitter/Analytics" - send session data to appropriate page
  const handleOpenInFitter = (session) => {
    const route = session.session_type === 'analytics' ? '/analytics' : '/fitter';
    navigate(route, {
      state: {
        prefillData: {
          club_type: session.club_type,
          club_speed: session.club_speed,
          ball_speed: session.ball_speed,
          launch_angle: session.launch_angle,
          spin_rate: session.spin_rate,
          attack_angle: session.attack_angle,
          spin_axis: session.spin_axis,
        }
      }
    });
  };

  // Filter sessions by type
  const analyticsSessions = sessions.filter(s => s.session_type === 'analytics');
  const fitterSessions = sessions.filter(s => s.session_type === 'fitter');

  const renderSessionCard = (session) => (
    <Card key={session.id} className="overflow-hidden p-0">
      {/* Session Header */}
      <div
        className="p-5 cursor-pointer hover:bg-surface-light transition-colors"
        onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
      >
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-heading font-bold text-text-primary text-lg">{session.club_type}</span>
              {session.session_type && (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  session.session_type === 'analytics'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                }`}>
                  {session.session_type === 'analytics' ? 'Analytics' : 'Fitter'}
                </span>
              )}
              <span className="text-2xl font-mono text-primary">{session.carry} <span className="text-sm text-text-muted">yds</span></span>
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {session.date} · {session.input_method || 'manual'}
              {session.shot_count > 0 ? ` · ${session.shot_count} shots` : ''}
              {session.launch_angle != null && ` · ${session.launch_angle.toFixed(1)}° launch`}
            </div>
          </div>
          <ChevronDown size={20} className={`text-text-muted transition-transform ${expandedSession === session.id ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Expanded Session Details (Session History Receipt) */}
      {expandedSession === session.id && (
        <div className="border-t border-border bg-background/50">
          {/* Input Stats Grid */}
          <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-text-muted text-xs uppercase font-medium">Club Speed</div>
              <div className="font-mono text-text-primary">{session.club_speed != null ? session.club_speed : '-'} <span className="text-text-muted">mph</span></div>
            </div>
            <div>
              <div className="text-text-muted text-xs uppercase font-medium">Ball Speed</div>
              <div className="font-mono text-text-primary">{session.ball_speed != null ? session.ball_speed : '-'} <span className="text-text-muted">mph</span></div>
            </div>
            <div>
              <div className="text-text-muted text-xs uppercase font-medium">Launch Angle</div>
              <div className="font-mono text-text-primary">{session.launch_angle != null ? session.launch_angle : '-'}<span className="text-text-muted">°</span></div>
            </div>
            <div>
              <div className="text-text-muted text-xs uppercase font-medium">Spin Rate</div>
              <div className="font-mono text-text-primary">{session.spin_rate != null ? session.spin_rate : '-'} <span className="text-text-muted">rpm</span></div>
            </div>
          </div>

          {/* Recommendation (if saved) */}
          {(session.recommended_head || session.recommended_shaft) && (
            <div className="px-5 pb-4">
              <div className="bg-surface-light rounded-lg p-4 border border-border">
                <div className="text-xs text-text-muted uppercase mb-2 font-medium">Recommended Build</div>
                <div className="text-text-primary font-semibold">{session.recommended_head || 'N/A'}</div>
                {session.recommended_shaft && <div className="text-text-secondary text-sm mt-1">+ {session.recommended_shaft}</div>}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="px-5 pb-5 flex gap-3">
            <Button
              onClick={() => handleOpenInFitter(session)}
              variant="primary"
              size="md"
              icon={Crosshair}
              className="flex-1"
            >
              Open in {session.session_type === 'analytics' ? 'Analytics' : 'Fitter'}
            </Button>
            <Button
              onClick={() => downloadSession(session.id)}
              variant="secondary"
              size="md"
              icon={Download}
            >
              CSV
            </Button>
            <Button
              onClick={(e) => deleteSession(session.id, e)}
              variant="tertiary"
              size="md"
              icon={Trash2}
              className="hover:text-error"
            />
          </div>
        </div>
      )}
    </Card>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header with Export Button */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-heading font-bold flex items-center gap-3 text-text-primary">
          <History className="text-primary" size={24} /> Session History
        </h2>
        {sessions.length > 0 && (
          <Button
            onClick={exportAllSessions}
            variant="secondary"
            size="md"
            icon={Download}
          >
            Export All ({sessions.length})
          </Button>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="p-12 text-center"><RefreshCw className="animate-spin mx-auto text-text-muted" size={24} /></div>
      ) : sessions.length === 0 ? (
        <Card className="p-12 text-center">
          <History size={48} className="mx-auto text-text-muted mb-4" />
          <p className="text-text-secondary">No sessions yet. Run a fit and save it to build your log book.</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Analytics Sessions Section */}
          {analyticsSessions.length > 0 && (
            <div>
              <h3 className="text-lg font-heading font-semibold text-text-primary mb-4 flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-sm font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  Analytics
                </span>
                <span className="text-text-muted text-sm">({analyticsSessions.length})</span>
              </h3>
              <div className="space-y-4">
                {analyticsSessions.map(session => renderSessionCard(session))}
              </div>
            </div>
          )}

          {/* Fitter Sessions Section */}
          {fitterSessions.length > 0 && (
            <div>
              <h3 className="text-lg font-heading font-semibold text-text-primary mb-4 flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-sm font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  Fitter
                </span>
                <span className="text-text-muted text-sm">({fitterSessions.length})</span>
              </h3>
              <div className="space-y-4">
                {fitterSessions.map(session => (
                  renderSessionCard(session)
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
